/**
 * Tenant + listing driven UI flags for PostListingForm (purpose/property type, edit-mode categories).
 * Pure: safe to call from useMemo.
 */
export function resolvePostListingFormUiFlags({ tenantConstants, listingRecord, isDailyRental }) {
  const isLocaleAwareGenerateContent = tenantConstants?.LOCALE_AWARE_GENERATE_CONTENT_FIELDS === true;
  const isLocaleSpecificTitleDescription = tenantConstants?.LOCALE_SPECIFIC_TITLE_DESCRIPTION === true;
  const tenantFormSettings = tenantConstants.FORM || {};

  const shouldShowPurposeSelector = tenantFormSettings.HIDE_PURPOSE_SELECT !== true;
  const usePropertyTypeDropdown = tenantFormSettings.PROPERTY_TYPE_AS_SELECT === true;
  const isListingLinkedToProject = Boolean(listingRecord?.project?.id);
  // Inventory-mapped (unit-type) listings keep an editable Property Type control; plain
  // project-mapped listings do not (their type is fixed and already shown on the card).
  const isListingMappedToInventory = Boolean(listingRecord?.is_unit_type_listing);

  const shouldRenderPropertyTypeAsSelect =
    usePropertyTypeDropdown && !isDailyRental && !isListingLinkedToProject;

  const usePayloadCategoriesForPropertyTypeOnEdit =
    tenantFormSettings.USE_LISTING_CATEGORIES_FOR_PROPERTY_TYPE_ON_EDIT === true;

  const projectPropertyTypeExternalIdOrder = Array.isArray(tenantFormSettings.PROJECT_LISTING_PROPERTY_TYPE_ORDER)
    ? tenantFormSettings.PROJECT_LISTING_PROPERTY_TYPE_ORDER
    : null;

  const propertyTypeLabelTranslationKey =
    tenantFormSettings.PROPERTY_TYPE_FIELD_LABEL || 'Select Property Type';

  const isEditingExistingListing = Boolean(listingRecord?.id);
  const payloadListingCategories = listingRecord?.listing_categories;

  const shouldPreferPayloadCategoriesForPropertyType =
    isEditingExistingListing &&
    usePayloadCategoriesForPropertyTypeOnEdit &&
    Array.isArray(payloadListingCategories);

  const hasEmptyPayloadListingCategories =
    shouldPreferPayloadCategoriesForPropertyType && payloadListingCategories.length === 0;

  const lockPurposeAndPropertyTypeOnEdit =
    tenantFormSettings.DISABLE_PURPOSE_AND_PROPERTY_TYPE_ON_EDIT === true && isEditingExistingListing;
  // Top-level (not under FORM): the mere presence of `FORM` enables the Bayut redesign UI elsewhere.
  const lockPropertyTypeOnlyOnEdit =
    tenantConstants?.DISABLE_PROPERTY_TYPE_ON_EDIT === true && isEditingExistingListing;

  const isPurposeControlDisabled = lockPurposeAndPropertyTypeOnEdit;
  const isPropertyTypeControlDisabledOnEdit =
    (lockPurposeAndPropertyTypeOnEdit || lockPropertyTypeOnlyOnEdit) && !isListingLinkedToProject;

  const hidePropertyTypeWhenDisabled = tenantFormSettings.HIDE_PROPERTY_TYPE_WHEN_DISABLED === true;

  return {
    isLocaleAwareGenerateContent,
    isLocaleSpecificTitleDescription,
    shouldShowPurposeSelector,
    shouldRenderPropertyTypeAsSelect,
    shouldPreferPayloadCategoriesForPropertyType,
    projectPropertyTypeExternalIdOrder,
    propertyTypeLabelTranslationKey,
    isEditingExistingListing,
    payloadListingCategories,
    hasEmptyPayloadListingCategories,
    isListingLinkedToProject,
    isListingMappedToInventory,
    isPurposeControlDisabled,
    isPropertyTypeControlDisabledOnEdit,
    hidePropertyTypeWhenDisabled,
  };
}
